import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Card,
  CardHeader,
  CardContent,
  Switch,
  FormControlLabel,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import { AutoMode, ExpandMore, Email } from '@mui/icons-material';
import EmailForwardingSettings from './EmailForwardingSettings';
import GmailConnection from './GmailConnection';
import StatementPasswords from './StatementPasswords';
import { useUserSettings } from '../hooks/useUserSettings';
import { useNotification } from '../hooks/useNotification';

/**
 * Settings > Email: the forwarding address first (the main way in), how
 * forwarded emails are processed, then the optional Gmail connection.
 */
const EmailSettings = () => {
  const { settings, update } = useUserSettings();
  const { showSuccess, showError } = useNotification();
  const [saving, setSaving] = useState(false);
  // Opened on the Gmail section when coming back from connecting Gmail
  const [searchParams] = useSearchParams();

  const handleAutoProcess = async (event) => {
    const enabled = event.target.checked;
    setSaving(true);
    try {
      await update({ autoProcessForwarded: enabled });
      showSuccess(enabled ? 'Forwarded emails will be processed automatically' : 'Automatic processing turned off');
    } catch (error) {
      showError('Failed to save the setting');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      <EmailForwardingSettings />

      <Card sx={{ mb: 3 }}>
        <CardHeader
          avatar={<AutoMode color={settings?.autoProcessForwarded ? 'primary' : 'disabled'} />}
          title="Forwarded emails"
        />
        <CardContent>
          <FormControlLabel
            control={
              <Switch
                checked={!!settings?.autoProcessForwarded}
                onChange={handleAutoProcess}
                disabled={!settings || saving}
              />
            }
            label={
              <Box>
                <Typography variant="body1">Process forwarded emails automatically</Typography>
                <Typography variant="body2" color="text.secondary">
                  A couple of minutes after an email arrives it is read, 10 at a time. Bills, due dates, balances
                  and bill payments are updated straight away; transactions wait for you on the Review page.
                </Typography>
              </Box>
            }
          />
        </CardContent>
      </Card>

      <StatementPasswords />

      <Accordion disableGutters variant="outlined" defaultExpanded={searchParams.get('gmail') === '1'}>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Email color="action" />
            <Box>
              <Typography variant="subtitle1">Import from Gmail (optional)</Typography>
              <Typography variant="body2" color="text.secondary">
                Connect Gmail to import past emails or run a daily import
              </Typography>
            </Box>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <GmailConnection />
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};

export default EmailSettings;
